import { UNIT_CODES } from '@financiar/shared';
import type { CreateProductInput, UpdateProductInput } from '@financiar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Drawer, Form, Input, Select, Space, Switch } from 'antd';
import { useEffect, useMemo } from 'react';
import { getApiError } from '../../api/client';
import { applyServerErrors, undefinedIfEmpty } from '../../lib/forms';
import { formatNumber, MoneyInput } from '../../lib/format';
import type { VatRate } from '../../api/types';
import { createProdus, fetchProdus, updateProdus } from './api';

const UNIT_OPTIONS = UNIT_CODES.map((unit) => ({ value: unit.code, label: unit.label }));
const VAT_CATEGORY_LABELS: Record<string, string> = {
  STANDARD: 'Standard',
  REDUCED: 'Redusă',
  SUPER_REDUCED: 'Redusă (super)',
};
const VAT_CATEGORY_ORDER = ['STANDARD', 'REDUCED', 'SUPER_REDUCED'] as const;

interface ProdusFormValues {
  name: string;
  description?: string;
  unitCode: string;
  unitPrice: string;
  vatCategory: string;
  isActive: boolean;
}

const CREATE_DEFAULTS: Partial<ProdusFormValues> = { unitCode: 'H87', vatCategory: 'STANDARD', isActive: true };

/** `/produse`'s create/edit Drawer. `vatRates` (from `GET /vat-rates`, fetched
 * once by ProduseListPage and passed down) supplies the current percent
 * shown in each category's Select label — e.g. "Standard (21%)". */
export function ProdusFormDrawer({
  open,
  firmaId,
  editingId,
  vatRates,
  onClose,
}: {
  open: boolean;
  firmaId: string;
  editingId: string | null;
  vatRates: VatRate[];
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ProdusFormValues>();
  const isEdit = editingId !== null;

  const vatCategoryOptions = useMemo(() => {
    const rateByCategory = new Map(vatRates.map((rate) => [rate.category as string, rate.ratePercent]));
    return VAT_CATEGORY_ORDER.map((category) => {
      const rate = rateByCategory.get(category);
      const label = rate ? `${VAT_CATEGORY_LABELS[category]} (${formatNumber(rate)}%)` : VAT_CATEGORY_LABELS[category];
      return { value: category, label };
    });
  }, [vatRates]);

  const { data: produs, isLoading: isLoadingProdus } = useQuery({
    queryKey: ['products', 'detail', editingId],
    queryFn: () => fetchProdus(editingId as string),
    enabled: open && isEdit,
  });

  useEffect(() => {
    if (!open) return;
    if (isEdit && produs) {
      form.setFieldsValue({
        name: produs.name,
        description: produs.description ?? undefined,
        unitCode: produs.unitCode,
        unitPrice: produs.unitPrice,
        vatCategory: produs.vatCategory,
        isActive: produs.isActive,
      });
    } else if (!isEdit) {
      form.resetFields();
      form.setFieldsValue(CREATE_DEFAULTS);
    }
  }, [open, isEdit, produs, form]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['products', firmaId] });

  const createMutation = useMutation({
    mutationFn: (values: CreateProductInput) => createProdus(values),
    onSuccess: () => {
      message.success('Produsul a fost creat.');
      invalidate();
      onClose();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Produsul nu a putut fi creat.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: UpdateProductInput) => updateProdus(editingId as string, values),
    onSuccess: () => {
      message.success('Produsul a fost actualizat.');
      invalidate();
      onClose();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Produsul nu a putut fi actualizat.');
    },
  });

  function handleFinish(values: ProdusFormValues) {
    if (isEdit) {
      updateMutation.mutate({
        name: values.name.trim(),
        description: undefinedIfEmpty(values.description),
        unitCode: values.unitCode,
        unitPrice: values.unitPrice,
        vatCategory: values.vatCategory,
        isActive: values.isActive,
      } as UpdateProductInput);
    } else {
      createMutation.mutate({
        companyId: firmaId,
        name: values.name.trim(),
        description: undefinedIfEmpty(values.description),
        unitCode: values.unitCode,
        unitPrice: values.unitPrice,
        vatCategory: values.vatCategory,
        isActive: values.isActive,
      } as CreateProductInput);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Drawer
      title={isEdit ? 'Editează produsul' : 'Adaugă produs'}
      width="clamp(560px, 40vw, 960px)"
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Închide</Button>
          <Button type="primary" onClick={() => form.submit()} loading={isSaving}>
            Salvează
          </Button>
        </Space>
      }
    >
      <Form<ProdusFormValues> form={form} layout="vertical" onFinish={handleFinish} disabled={isEdit && isLoadingProdus}>
        <Form.Item name="name" label="Denumire" rules={[{ required: true, message: 'Denumirea este obligatorie.' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Descriere">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="unitCode" label="Unitate de măsură" rules={[{ required: true }]}>
          <Select options={UNIT_OPTIONS} />
        </Form.Item>
        <Form.Item name="unitPrice" label="Preț unitar" rules={[{ required: true, message: 'Prețul este obligatoriu.' }]}>
          <MoneyInput />
        </Form.Item>
        <Form.Item name="vatCategory" label="Categorie TVA" rules={[{ required: true }]}>
          <Select options={vatCategoryOptions} />
        </Form.Item>
        <Form.Item name="isActive" label="Activ" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
